import { chakra } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { Icon, type LucideIcon } from '../icons/Icon';
import { Heading } from '../primitives/Heading';
import { Text } from '../primitives/Text';

const Center = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2',
    maxWidth: 'content.narrow',
    mx: 'auto',
    py: '12',
    px: '6',
    textAlign: 'center',
  },
});

const IconWrap = chakra('div', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: '12',
    borderRadius: 'full',
    mb: '2',
  },
});

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  /** Primary call to action (e.g. a Button). */
  action?: ReactNode;
  /** `brand` for a key, on-brand empty moment (pink); `neutral` otherwise. */
  tone?: 'neutral' | 'brand';
}

/** The canonical "nothing here yet" surface. */
export function EmptyState({ icon, title, description, action, tone = 'neutral' }: EmptyStateProps) {
  return (
    <Center>
      {icon && (
        <IconWrap bg={tone === 'brand' ? 'brand.subtle' : 'surface.subtle'}>
          <Icon as={icon} size="lg" tone={tone === 'brand' ? 'brand' : 'muted'} />
        </IconWrap>
      )}
      <Heading level={2} size="md">
        {title}
      </Heading>
      {description && (
        <Text tone="muted" size="md">
          {description}
        </Text>
      )}
      {action && <chakra.div mt="4">{action}</chakra.div>}
    </Center>
  );
}
